import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle, ClipboardList,
  FileSignature, Undo2, UserRound, MessageSquare, Calendar,
} from 'lucide-react';
import api from '@/lib/axios';
import { useAuth } from '@/context/AuthContext';
import DataQualityWidget from '@/pages/Dashboard/DataQualityWidget';
import { ReminderOpsCard } from '@/pages/Dashboard/PersonRemindersPanel';
import { toast } from 'sonner';

function fmtDate(iso) {
  if (!iso) return '—';
  const d = String(iso).slice(0, 10);
  const [y, m, day] = d.split('-');
  if (!y || !m || !day) return iso;
  return `${day}.${m}.${y}`;
}

function weekdayLong(d = new Date()) {
  return d.toLocaleDateString('de-DE', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

function urgencyBarClass(urgency) {
  if (urgency === 'overdue') return 'border-l-4 border-l-brand-red';
  if (urgency === 'today') return 'border-l-4 border-l-transparent';
  return 'border-l-4 border-l-slate-400';
}

function OpsUrgencyDot({ show }) {
  if (!show) return null;
  return (
    <span
      data-testid="ops-urgency-dot"
      className="absolute left-2.5 top-3 h-2 w-2 rounded-full bg-brand-red"
      aria-hidden
    />
  );
}

function OpsCard({ title, subtitle, dateLabel, onOpen, urgency = 'week' }) {
  const isToday = urgency === 'today';
  return (
    <button
      type="button"
      onClick={onOpen}
      data-urgency={urgency}
      className={`relative w-full text-left bg-white rounded-lg border border-slate-200 ${urgencyBarClass(urgency)} px-3 py-2.5 shadow-sm hover:shadow-md hover:border-slate-300 transition-all`}
    >
      <OpsUrgencyDot show={isToday} />
      <div className={isToday ? 'pl-3' : undefined}>
        {dateLabel && <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-0.5">{dateLabel}</div>}
        <div className="text-sm font-semibold text-slate-800 leading-snug">{title}</div>
        {subtitle && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{subtitle}</div>}
      </div>
    </button>
  );
}

function AlertTile({ icon: Icon, title, badge, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="relative w-full min-w-0 bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-5 hover:border-brand-red/30 hover:shadow-md transition-all text-left"
    >
      {badge > 0 && (
        <span
          className="absolute top-2 right-2 min-w-[1.5rem] h-6 px-1.5 rounded-full bg-brand-red text-white text-xs font-bold flex items-center justify-center"
          data-testid={`${testId}-badge`}
        >
          {badge}
        </span>
      )}
      <div className="flex items-start gap-3 min-w-0">
        <div className="p-2 rounded-lg bg-brand-red/5 shrink-0">
          <Icon className="w-6 h-6 text-brand-red" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-800 leading-snug break-words" data-testid={testId ? `${testId}-title` : undefined}>{title}</div>
          <div className="text-[11px] text-slate-500 mt-1">Zum Vorgang öffnen</div>
        </div>
      </div>
    </button>
  );
}

function KpiCell({ label, value, onClick, emphasize, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-1 min-w-[5.5rem] px-2 py-2 text-center hover:bg-slate-50 rounded-md transition-colors"
    >
      <div
        data-testid={testId}
        className={`tabular-nums text-slate-800 ${emphasize ? 'text-2xl font-bold' : 'text-xl font-bold'}`}
      >
        {(value ?? 0).toLocaleString('de-DE')}
      </div>
      <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">{label}</div>
    </button>
  );
}

/**
 * MSV-ähnliche Startseiten-Board: Welcome, KPIs, Alert-Karten, Vorgänge, Datenqualität.
 * Mit `slot={({ welcome, blocks }) => ...}` liefert die Seite die Blöcke zur freien Anordnung.
 */
export default function HomeOpsBoard({
  canStudents,
  canEnrollments,
  canLessons,
  canReports,
  canCourses,
  canMessages = false,
  isAdminish,
  showDataQuality = true,
  isTeacher = false,
  footerParams,
  showActiveOnly,
  isVisible = () => true,
  slot,
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [home, setHome] = useState({
    enrollments: 0,
    courses: 0,
    prepaid: 0,
    trialOpen: 0,
    students: 0,
    studentsTotal: 0,
    regConfirmed: 0,
    regPending: 0,
    absences: 0,
    terminations: 0,
    trialRequests: 0,
    todayLessons: 0,
    unreadMessages: 0,
  });
  const [ops, setOps] = useState({ overdue: [], today: [], week: [] });

  const displayName = user?.full_name || user?.username || 'Admin';

  useEffect(() => {
    let cancelled = false;
    const todayIso = () => {
      const n = new Date();
      const p = (x) => String(x).padStart(2, '0');
      return `${n.getFullYear()}-${p(n.getMonth() + 1)}-${p(n.getDate())}`;
    };
    const load = async () => {
      const empty = (data) => Promise.resolve({ data });
      if (isTeacher) {
        const [response, unreadRes] = await Promise.all([
          api.get('/teacher/dashboard').catch(() => empty({})),
          canMessages
            ? api.get('/messages/unread-count').catch(() => empty({ unread: 0 }))
            : empty({ unread: 0 }),
        ]);
        if (cancelled) return;
        const data = response.data || {};
        setHome((current) => ({
          ...current,
          enrollments: data.enrollments_active || 0,
          courses: data.courses_active || 0,
          students: data.students_active || 0,
          studentsTotal: data.students_active || 0,
          todayLessons: data.lessons_today || 0,
          unreadMessages: unreadRes.data?.unread || 0,
        }));
        setOps({ overdue: [], today: [], week: [] });
        return;
      }
      const today = todayIso();
      const [
        kpis, students, prepaid, trials, regSummary, selfSummary,
        absences, trialReqs, terms, regs, remBoard, unreadRes, todayPlanRes,
      ] = await Promise.all([
        canReports
          ? api.get('/reports/kpis', { params: footerParams }).catch(() => empty({}))
          : empty({}),
        canStudents
          ? api.get('/students/stats/count', { params: footerParams }).catch(() => empty({}))
          : empty({}),
        canEnrollments || canReports
          ? api.get('/prepaid-cards/stats').catch(() => empty({}))
          : empty({}),
        canLessons
          ? api.get('/trial-lessons/stats').catch(() => empty({}))
          : empty({}),
        isAdminish
          ? api.get('/admin/registrations/summary').catch(() => empty({}))
          : empty({}),
        isAdminish
          ? api.get('/admin/self-service/summary').catch(() => empty({}))
          : empty({}),
        isAdminish
          ? api.get('/admin/self-service/absences', { params: { status: 'reported', limit: 30 } }).catch(() => empty([]))
          : empty([]),
        isAdminish
          ? api.get('/admin/self-service/trial-lessons', { params: { status: 'new', limit: 30 } }).catch(() => empty([]))
          : empty([]),
        isAdminish
          ? api.get('/admin/self-service/terminations', { params: { status: 'submitted', limit: 30 } }).catch(() => empty([]))
          : empty([]),
        isAdminish
          ? api.get('/admin/registrations', { params: { status: 'confirmed', limit: 30 } }).catch(() => empty({ items: [] }))
          : empty({ items: [] }),
        api.get('/person-reminders/board', { params: { days: 7 } }).catch(() => empty({ overdue: [], today: [], week: [] })),
        canMessages
          ? api.get('/messages/unread-count').catch(() => empty({ unread: 0 }))
          : empty({ unread: 0 }),
        canLessons
          ? api.get('/lessons/plan-range', {
            params: { start_date: today, end_date: today, include_students: false },
          }).catch(() => empty({ entries: [] }))
          : empty({ entries: [] }),
      ]);

      if (cancelled) return;

      const todayEntries = (todayPlanRes.data?.entries || []).filter((e) => !e.cancelled);
      const trialOpen = (trials.data?.open || 0) + (trials.data?.reserved || 0);
      setHome({
        enrollments: kpis.data?.enrollments_active || 0,
        courses: kpis.data?.courses_with_enrollment || kpis.data?.courses_active || 0,
        prepaid: prepaid.data?.active_cards ?? prepaid.data?.total_cards ?? 0,
        trialOpen,
        students: students.data?.with_enrollment || 0,
        studentsTotal: students.data?.total || students.data?.active || 0,
        regConfirmed: regSummary.data?.confirmed || 0,
        regPending: regSummary.data?.pending || 0,
        absences: selfSummary.data?.open_absences || 0,
        terminations: selfSummary.data?.pending_terminations || 0,
        trialRequests: selfSummary.data?.new_trial_requests || 0,
        todayLessons: todayEntries.length,
        unreadMessages: unreadRes.data?.unread || 0,
      });

      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const in7 = new Date(todayStart);
      in7.setDate(in7.getDate() + 7);

      const cards = [];
      const absList = Array.isArray(absences.data) ? absences.data : [];
      for (const a of absList) {
        cards.push({
          id: `abs-${a.id}`,
          kind: 'absence',
          title: a.student_name || 'Abwesenheit',
          subtitle: a.reason || 'Abwesenheitsmeldung',
          date: a.absence_date || a.created_at,
          href: '/self-service-inbox',
        });
      }
      const trList = Array.isArray(trialReqs.data) ? trialReqs.data : [];
      for (const t of trList) {
        const tName = [t.student_first_name, t.student_last_name].filter(Boolean).join(' ')
          || t.guardian_name || 'Probestunde';
        cards.push({
          id: `trl-${t.id}`,
          kind: 'trial',
          title: tName,
          subtitle: t.subject_preference || 'Probestunden-Anfrage',
          date: t.created_at,
          href: '/self-service-inbox',
        });
      }
      const termList = Array.isArray(terms.data) ? terms.data : [];
      for (const t of termList) {
        cards.push({
          id: `term-${t.id}`,
          kind: 'termination',
          title: t.student_name || [t.student_first_name, t.student_last_name].filter(Boolean).join(' ') || 'Kündigung',
          subtitle: t.reason || t.admin_notes || 'Kündigungsantrag',
          date: t.effective_date || t.created_at,
          href: '/self-service-inbox',
        });
      }
      const regItems = regs.data?.items || regs.data || [];
      const regList = Array.isArray(regItems) ? regItems : [];
      for (const r of regList) {
        const name = [r.student_first_name || r.payload?.student?.first_name,
          r.student_last_name || r.payload?.student?.last_name].filter(Boolean).join(' ')
          || r.payer_email || 'Online-Anmeldung';
        cards.push({
          id: `reg-${r.id}`,
          kind: 'registration',
          title: name,
          subtitle: 'Bestätigte Online-Anmeldung',
          date: r.confirmed_at || r.created_at,
          href: '/registrations-inbox',
        });
      }

      const parseDay = (v) => {
        if (!v) return null;
        const d = new Date(String(v).slice(0, 10));
        if (Number.isNaN(d.getTime())) return null;
        d.setHours(0, 0, 0, 0);
        return d;
      };

      const mapReminder = (r) => ({
        id: `rem-${r.id}`,
        reminderId: r.id,
        kind: 'reminder',
        title: r.person_name || 'Erinnerung',
        person_name: r.person_name,
        subtitle: r.text,
        text: r.text,
        date: r.due_at,
        due_at: r.due_at,
        href: r.href || '/',
        created_by_name: r.created_by_name,
        updated_by_name: r.updated_by_name,
        created_at: r.created_at,
        updated_at: r.updated_at,
      });

      const rem = remBoard.data || {};
      const overdue = (rem.overdue || []).map(mapReminder);
      const todayItems = (rem.today || []).map(mapReminder);
      const week = (rem.week || []).map(mapReminder);

      for (const c of cards) {
        const d = parseDay(c.date);
        if (!d) {
          week.push(c);
          continue;
        }
        if (d < todayStart) overdue.push(c);
        else if (d.getTime() === todayStart.getTime()) todayItems.push(c);
        else week.push(c);
      }

      setOps({ overdue, today: todayItems, week });
    };
    load();
    return () => { cancelled = true; };
  }, [canStudents, canEnrollments, canLessons, canReports, canCourses, canMessages, isAdminish, isTeacher, footerParams]);

  const reloadOps = async () => {
    try {
      const rem = await api.get('/person-reminders/board', { params: { days: 7 } });
      const mapReminder = (r) => ({
        id: `rem-${r.id}`,
        reminderId: r.id,
        kind: 'reminder',
        title: r.person_name || 'Erinnerung',
        person_name: r.person_name,
        subtitle: r.text,
        text: r.text,
        date: r.due_at,
        due_at: r.due_at,
        href: r.href || '/',
        created_by_name: r.created_by_name,
        updated_by_name: r.updated_by_name,
        created_at: r.created_at,
        updated_at: r.updated_at,
      });
      const stripWorkflow = (list) => (list || []).filter((c) => c.kind !== 'reminder');
      setOps((prev) => ({
        overdue: [...(rem.data?.overdue || []).map(mapReminder), ...stripWorkflow(prev.overdue)],
        today: [...(rem.data?.today || []).map(mapReminder), ...stripWorkflow(prev.today)],
        week: [...(rem.data?.week || []).map(mapReminder), ...stripWorkflow(prev.week)],
      }));
    } catch { /* ignore */ }
  };

  const completeReminder = async (item) => {
    try {
      await api.post(`/person-reminders/${item.reminderId}/complete`);
      toast.success('Erinnerung erledigt');
      reloadOps();
    } catch {
      toast.error('Konnte nicht abgehakt werden');
    }
  };

  const studentKpi = showActiveOnly ? home.students : home.studentsTotal;
  const alertTrials = home.trialOpen + home.trialRequests;
  const alertRegs = home.regConfirmed + home.regPending;

  const columns = useMemo(() => ([
    { key: 'overdue', visKey: 'ops_overdue', title: 'Überfällig', items: ops.overdue },
    { key: 'today', visKey: 'ops_today', title: 'Heute', items: ops.today },
    { key: 'week', visKey: 'ops_week', title: 'Innerhalb von 7 Tagen', items: ops.week },
  ]), [ops]);

  const visibleColumns = isTeacher ? [] : columns.filter((c) => isVisible(c.visKey));
  const showOpsGrid = visibleColumns.length > 0;

  const kpiCells = [
    !isTeacher && canEnrollments && isVisible('kpi_enrollments') && (
      <KpiCell key="enrollments" label="Belegungen" value={home.enrollments} testId="home-kpi-enrollments" onClick={() => navigate('/unterrichtsverwaltung')} />
    ),
    canCourses && isVisible('kpi_courses') && (
      <KpiCell key="courses" label={isTeacher ? 'Meine Kurse' : 'Kurse'} value={home.courses} onClick={() => navigate('/courses')} />
    ),
    !isTeacher && isVisible('kpi_prepaid') && (
      <KpiCell key="prepaid" label="Prepaid-Karten" value={home.prepaid} onClick={() => navigate('/prepaid-cards')} />
    ),
    canLessons && isVisible('kpi_trials') && (
      <KpiCell
        key="trials"
        label={isTeacher ? 'Heute' : 'P/S-Stunden'}
        value={isTeacher ? home.todayLessons : home.trialOpen}
        onClick={() => navigate(isTeacher ? '/calendar?date=today' : '/trial-lessons')}
      />
    ),
    canStudents && isVisible('kpi_students') && (
      <KpiCell
        key="students"
        label={isTeacher ? 'Meine Schüler:innen' : 'Schüler:innen'}
        value={studentKpi}
        onClick={() => navigate(showActiveOnly ? '/students?is_active=true' : '/students')}
      />
    ),
    !isTeacher && canStudents && isVisible('kpi_total') && (
      <KpiCell key="total" label="Gesamt" value={home.studentsTotal} onClick={() => navigate('/students')} emphasize />
    ),
  ].filter(Boolean);

  const openTodayCalendar = () => {
    navigate('/calendar?date=today&view=resources-vertical&rooms=all&location=all&begin=7');
  };

  const alertTiles = [
    !isTeacher && canLessons && isVisible('alert_trials') && (
      <AlertTile
        key="trials"
        icon={UserRound}
        title="Probe-/Schnupperstunden"
        badge={alertTrials}
        onClick={() => navigate(home.trialRequests > 0 ? '/self-service-inbox' : '/trial-lessons')}
        testId="home-alert-trials"
      />
    ),
    isAdminish && isVisible('alert_registrations') && (
      <AlertTile
        key="regs"
        icon={ClipboardList}
        title="Online-Anmeldungen"
        badge={alertRegs}
        onClick={() => navigate('/registrations-inbox')}
        testId="home-alert-registrations"
      />
    ),
    isAdminish && isVisible('alert_deregistrations') && (
      <AlertTile
        key="deregs"
        icon={FileSignature}
        title="Online-Abmeldungen"
        badge={home.terminations}
        onClick={() => navigate('/self-service-inbox?tab=terminations')}
        testId="home-alert-deregistrations"
      />
    ),
    isAdminish && isVisible('alert_withdrawals') && (
      <AlertTile
        key="withdrawals"
        icon={Undo2}
        title="Online-Widerrufe"
        badge={0}
        onClick={() => navigate('/self-service-inbox?tab=terminations')}
        testId="home-alert-withdrawals"
      />
    ),
    canMessages && isVisible('alert_messages') && (
      <AlertTile
        key="messages"
        icon={MessageSquare}
        title="Nachrichten"
        badge={home.unreadMessages}
        onClick={() => navigate('/nachrichten')}
        testId="home-alert-messages"
      />
    ),
    isVisible('alert_calendar') && (
      <AlertTile
        key="calendar"
        icon={Calendar}
        title="Kalender"
        badge={home.todayLessons}
        onClick={openTodayCalendar}
        testId="home-alert-calendar"
      />
    ),
  ].filter(Boolean);

  const welcome = (
    <div className="mb-5">
      <h1 className="text-2xl sm:text-3xl font-semibold text-slate-800 tracking-tight">
        Willkommen, {displayName}!
      </h1>
      <p className="text-sm text-slate-500 mt-1 capitalize">{weekdayLong()}</p>
    </div>
  );

  const blocks = {
    kpi_strip: kpiCells.length > 0 ? (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="home-kpi-strip">
        <div className="flex flex-wrap divide-x divide-slate-100">
          {kpiCells}
        </div>
      </div>
    ) : null,
    alerts: alertTiles.length > 0 ? (
      <div
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 pt-1"
        data-testid="home-alert-tiles"
      >
        {alertTiles}
      </div>
    ) : null,
    ops_grid: showOpsGrid ? (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {visibleColumns.map((col) => (
          <div key={col.key} className="bg-slate-50/80 rounded-xl border border-slate-200 p-3 min-h-[180px]" data-testid={`home-ops-${col.key}`}>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2 px-0.5">{col.title}</h3>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {col.items.length === 0 ? (
                <p className="text-xs text-slate-400 italic py-4 text-center">Keine Einträge</p>
              ) : col.items.slice(0, 12).map((item) => (
                item.kind === 'reminder' ? (
                  <ReminderOpsCard
                    key={item.id}
                    item={item}
                    urgency={col.key}
                    onComplete={completeReminder}
                    onOpen={(it) => navigate(it.href)}
                  />
                ) : (
                  <OpsCard
                    key={item.id}
                    title={item.title}
                    subtitle={item.subtitle}
                    dateLabel={fmtDate(item.date)}
                    urgency={col.key}
                    onOpen={() => navigate(item.href)}
                  />
                )
              ))}
            </div>
          </div>
        ))}
      </div>
    ) : null,
    data_quality: !isTeacher && canReports && showDataQuality && isVisible('data_quality') ? (
      <div data-testid="home-data-quality">
        <div className="flex items-center gap-2 mb-2 px-1">
          <AlertCircle className="w-4 h-4 text-brand-red" />
          <h2 className="text-sm font-semibold text-slate-800">Vertrags- & Datenstatus mit Klärungsbedarf</h2>
        </div>
        <DataQualityWidget />
      </div>
    ) : null,
  };

  if (typeof slot === 'function') {
    return slot({ welcome, blocks });
  }

  return (
    <div className="space-y-5 mb-8" data-testid="msv-home-board">
      {welcome}
      {Object.entries(blocks).map(([key, node]) => node ? <React.Fragment key={key}>{node}</React.Fragment> : null)}
    </div>
  );
}
